import classNames from 'classnames';
import { useIsInternalUser } from 'hooks/useIsInternalUser';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import type { AppState } from 'state/state.model';
import { getSparkUrl } from 'state/user';
import styled from 'styled-components';

const StyledButton = styled.button`
  text-decoration: underline !important;
`;

export interface FileCodeLinkProps {
  valueToDisplay: string | React.JSX.Element;
  fileCode: string;
  className?: string;
  linkClassName?: string;
  iconClassName?: string;
  disabled?: boolean;
  isInternal?: boolean;
}

export const FileCodeLink: FC<FileCodeLinkProps> = ({
  valueToDisplay,
  fileCode,
  className,
  linkClassName,
  iconClassName,
  disabled,
  isInternal,
}) => {
  const { formatMessage } = useIntl();
  const [isHovered, setIsHovered] = useState(false);
  const isInternalUser = useIsInternalUser() || isInternal;
  const sparkUrl = useSelector((state: AppState) => getSparkUrl(state));
  const generateSparkUrl = useCallback(() => `${sparkUrl}${fileCode}`, [fileCode, sparkUrl]);
  const onMouseEnter = useCallback(() => setIsHovered(true), []);
  const onMouseLeave = useCallback(() => setIsHovered(false), []);
  const openInSparkTooltip = useMemo(
    () => formatMessage({ id: 'moreMenu.buttonOpenInSpark.fileCode.tooltip' }),
    [formatMessage],
  );
  return (
    <div
      className={classNames('d-flex flex-row align-items-center', className)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {isInternalUser && !disabled && (
        <StyledButton
          id="btnOpenInSpark"
          type="button"
          className={classNames('btn btn-link btn-sm', { invisible: !isHovered })}
          title={openInSparkTooltip}
          onClick={() => window.open(generateSparkUrl(), '_blank')}
        >
          <FormattedMessage id="openInSpark" />
        </StyledButton>
      )}
      <span>{valueToDisplay}</span>
      {isInternalUser && !disabled && (
        <a
          href={generateSparkUrl()}
          target="_blank"
          className={classNames('ms-1', linkClassName)}
          data-e2e="open-in-spark-url"
        >
          <i className={classNames('icon icon-sm', iconClassName)}>launch</i>
        </a>
      )}
    </div>
  );
};
